import type { LucideIcon } from 'lucide-react'
import {
  ClipboardList,
  FlaskConical,
  GraduationCap,
  Landmark,
  Library,
  MessageSquare,
  Package,
} from 'lucide-react'
import { MODULE_REGISTRY, type ModuleId } from './moduleRegistry'

export type HelpModuleCard = {
  id: string
  registryId?: ModuleId
  abbreviation: string
  title: string
  description: string
  accent: string
  iconBg: string
  icon?: string
  LucideIcon?: LucideIcon
  comingSoon?: boolean
  keywords: string[]
}

const registryIcon = (id: ModuleId) => MODULE_REGISTRY.find((mod) => mod.id === id)?.icon

export const HELP_MODULE_CATALOG: HelpModuleCard[] = [
  {
    id: 'cntr',
    registryId: 'cntr',
    abbreviation: 'CNTR',
    title: 'Exam Centre',
    description: 'Board exam centre operations, datesheets, seating, duties, and answer sheet tracking.',
    accent: 'from-rose-500 to-pink-500',
    iconBg: 'bg-rose-50 text-rose-600 ring-rose-100',
    icon: registryIcon('cntr'),
    keywords: ['cntr', 'exam centre', 'seating', 'candidates', 'cbse board'],
  },
  {
    id: 'timetable',
    registryId: 'timetable',
    abbreviation: 'TMTBL',
    title: 'Timetable',
    description: 'Class schedules, teacher allocation, bell timings, and period distribution.',
    accent: 'from-violet-500 to-purple-500',
    iconBg: 'bg-violet-50 text-violet-600 ring-violet-100',
    icon: registryIcon('timetable'),
    keywords: ['timetable', 'schedule', 'period', 'bell', 'teacher'],
  },
  {
    id: 'exmcl',
    registryId: 'exmcl',
    abbreviation: 'EXMCL',
    title: 'Exams & Marks',
    description: 'Internal exams, CBSE registration, marks entry, report cards, and award lists.',
    accent: 'from-blue-500 to-indigo-500',
    iconBg: 'bg-blue-50 text-blue-600 ring-blue-100',
    icon: registryIcon('exmcl'),
    keywords: ['exam', 'marks', 'report card', 'registration', 'result'],
  },
  {
    id: 'cpitl',
    abbreviation: 'CPITL',
    title: 'Capital Assets',
    description: 'Track school assets, depreciation, maintenance, and capital expenditure.',
    accent: 'from-amber-500 to-orange-500',
    iconBg: 'bg-amber-50 text-amber-600 ring-amber-100',
    LucideIcon: Landmark,
    comingSoon: true,
    keywords: ['capital', 'finance', 'assets', 'expense'],
  },
  {
    id: 'trnst',
    registryId: 'trnst',
    abbreviation: 'TRNST',
    title: 'Transport',
    description: 'Routes, vehicles, drivers, and student transport assignments.',
    accent: 'from-sky-500 to-cyan-500',
    iconBg: 'bg-sky-50 text-sky-600 ring-sky-100',
    icon: registryIcon('trnst'),
    keywords: ['transport', 'bus', 'route', 'vehicle'],
  },
  {
    id: 'actvt',
    registryId: 'actvt',
    abbreviation: 'ACTVT',
    title: 'Activities',
    description: 'Co-curricular houses, events, competitions, and activity tracking.',
    accent: 'from-fuchsia-500 to-pink-500',
    iconBg: 'bg-fuchsia-50 text-fuchsia-600 ring-fuchsia-100',
    icon: registryIcon('actvt'),
    keywords: ['activities', 'houses', 'events', 'sports'],
  },
  {
    id: 'acdmc',
    registryId: 'acdmc',
    abbreviation: 'ACDMC',
    title: 'Academics',
    description: 'Lesson plans, curriculum mapping, syllabus, and academic planning.',
    accent: 'from-emerald-500 to-teal-500',
    iconBg: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
    icon: registryIcon('acdmc'),
    keywords: ['academics', 'lesson', 'curriculum', 'syllabus'],
  },
  {
    id: 'sttok',
    abbreviation: 'STTOK',
    title: 'Store & Inventory',
    description: 'Inventory, stock movement, purchase requests, and store management.',
    accent: 'from-lime-500 to-green-500',
    iconBg: 'bg-lime-50 text-lime-600 ring-lime-100',
    LucideIcon: Package,
    comingSoon: true,
    keywords: ['store', 'inventory', 'stock'],
  },
  {
    id: 'attnd',
    registryId: 'attnd',
    abbreviation: 'ATTND',
    title: 'Attendance',
    description: 'Daily student and staff attendance, leave records, and attendance reports.',
    accent: 'from-green-500 to-emerald-500',
    iconBg: 'bg-green-50 text-green-600 ring-green-100',
    icon: registryIcon('attnd'),
    keywords: ['attendance', 'present', 'absent', 'leave'],
  },
  {
    id: 'lbry',
    abbreviation: 'LBRY',
    title: 'Library',
    description: 'Digital library catalogue, issue/return, and reading resources.',
    accent: 'from-indigo-500 to-blue-500',
    iconBg: 'bg-indigo-50 text-indigo-600 ring-indigo-100',
    LucideIcon: Library,
    comingSoon: true,
    keywords: ['library', 'books', 'issue', 'return'],
  },
  {
    id: 'labbs',
    abbreviation: 'LABBS',
    title: 'Lab & Resources',
    description: 'Lab bookings, equipment inventory, and practical session scheduling.',
    accent: 'from-cyan-500 to-teal-500',
    iconBg: 'bg-cyan-50 text-cyan-600 ring-cyan-100',
    LucideIcon: FlaskConical,
    comingSoon: true,
    keywords: ['lab', 'practical', 'equipment'],
  },
  {
    id: 'staaf',
    registryId: 'staaf',
    abbreviation: 'STAAF',
    title: 'Staff OS',
    description: 'Staff profiles, departments, roles, and human resource workflows.',
    accent: 'from-orange-500 to-amber-500',
    iconBg: 'bg-orange-50 text-orange-600 ring-orange-100',
    icon: registryIcon('staaf'),
    keywords: ['staff', 'hr', 'employee', 'department'],
  },
  {
    id: 'mdcl',
    registryId: 'mdcl',
    abbreviation: 'MDCL',
    title: 'Medical Clinic',
    description: 'Health records, clinic visits, medications, and student medical cases.',
    accent: 'from-red-500 to-rose-500',
    iconBg: 'bg-red-50 text-red-600 ring-red-100',
    icon: registryIcon('mdcl'),
    keywords: ['medical', 'clinic', 'health'],
  },
  {
    id: 'asets',
    registryId: 'asets',
    abbreviation: 'ASETS',
    title: 'Asset Management',
    description: 'School-owned physical assets: ownership, location, custodians, maintenance, audits, and disposal.',
    accent: 'from-teal-500 to-emerald-500',
    iconBg: 'bg-teal-50 text-teal-600 ring-teal-100',
    icon: registryIcon('asets'),
    keywords: ['asset', 'inventory', 'furniture', 'equipment', 'store', 'maintenance'],
  },
  {
    id: 'alumni',
    abbreviation: 'ALMNI',
    title: 'Alumni',
    description: 'Pass-out student records, alumni engagement, and reunion management.',
    accent: 'from-purple-500 to-violet-500',
    iconBg: 'bg-purple-50 text-purple-600 ring-purple-100',
    LucideIcon: GraduationCap,
    comingSoon: true,
    keywords: ['alumni', 'passout', 'graduate'],
  },
  {
    id: 'stdnt',
    registryId: 'stdnt',
    abbreviation: 'STDNT',
    title: 'Student OS',
    description: 'Student profiles, admissions, class-section matrix, and student records.',
    accent: 'from-blue-600 to-indigo-600',
    iconBg: 'bg-blue-50 text-blue-700 ring-blue-100',
    icon: registryIcon('stdnt'),
    keywords: ['student', 'admission', 'records', 'class', 'section'],
  },
  {
    id: 'cntct',
    abbreviation: 'CNTCT',
    title: 'Communication',
    description: 'Announcements, SMS/email alerts, and parent communication centre.',
    accent: 'from-teal-500 to-cyan-500',
    iconBg: 'bg-teal-50 text-teal-600 ring-teal-100',
    LucideIcon: MessageSquare,
    comingSoon: true,
    keywords: ['communication', 'sms', 'email', 'announcement'],
  },
  {
    id: 'fdbck',
    abbreviation: 'FDBCK',
    title: 'Feedback',
    description: 'Suggestions, complaints, and feedback collection from stakeholders.',
    accent: 'from-yellow-500 to-amber-500',
    iconBg: 'bg-yellow-50 text-yellow-700 ring-yellow-100',
    LucideIcon: ClipboardList,
    comingSoon: true,
    keywords: ['feedback', 'complaint', 'suggestion'],
  },
]

export const SUPPORT_CONTACT = {
  phone: '+91 1800-000-0000',
  email: 'support@capabble.cloud',
  chatHours: 'Mon–Sat, 9 AM – 6 PM IST',
} as const
